import { ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Parallax, ScrollReveal, StaggerContainer, StaggerItem, TextReveal } from '@/components/motion'
import { OfficeAppIcon, type OfficeApp } from '@/components/landing/OfficeAppIcon'
import { partners } from '@/data/about'
import { HALFTONE_STYLE, Highlight } from './Highlight'

const APPS: OfficeApp[] = ['word', 'excel', 'powerpoint', 'outlook']

/** Word window with the Templatean task pane, on a purple halftone field. */
function OfficeGraphic() {
  return (
    <div
      aria-hidden
      className="relative aspect-square overflow-hidden bg-[var(--color-landing-secondary)] [container-type:inline-size]"
    >
      <div className="absolute inset-0" style={HALFTONE_STYLE} />

      <Parallax className="absolute left-[9%] right-[9%] top-[13%]" distance={30}>
        <div className="bg-white shadow-[0_40px_70px_-30px_rgb(40_0_80_/_0.65)]">
          <div className="flex items-center gap-2 border-b border-[var(--color-landing-gray-200)] bg-[var(--color-landing-gray-50)] px-3 py-2">
            <OfficeAppIcon app="word" className="h-5 w-5" />
            <span className="text-[11px] font-semibold text-[var(--color-landing-gray-700)]">Client proposal</span>
          </div>
          <div className="grid grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
            {/* Document page */}
            <div className="border-r border-[var(--color-landing-gray-200)] p-[5cqw]">
              <div className="h-[2.4cqw] w-[70%] bg-[var(--color-landing-gray-900)]" />
              <div className="mt-[2cqw] h-[1.2cqw] w-[40%] bg-[var(--color-landing-accent)]" />
              {[96, 88, 92, 70, 0, 94, 86, 90, 64].map((w, i) =>
                w === 0 ? (
                  <div key={i} className="h-[3cqw]" />
                ) : (
                  <div key={i} className="mt-[1.6cqw] h-[1cqw] bg-[var(--color-landing-gray-200)]" style={{ width: `${w}%` }} />
                )
              )}
            </div>
            {/* Task pane */}
            <div className="p-[3cqw]">
              <p className="text-[clamp(10px,2.2cqw,14px)] font-bold tracking-[-0.01em] text-[var(--color-landing-gray-900)]">
                templ<span className="text-[var(--color-landing-accent)]">a</span>tean
              </p>
              <div className="mt-[2.5cqw] grid gap-[1.6cqw]">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="flex items-center gap-[1.4cqw] border border-[var(--color-landing-gray-200)] p-[1.4cqw]">
                    <span className={i === 0 ? 'h-[3cqw] w-[3cqw] shrink-0 bg-[var(--color-landing-accent)]' : 'h-[3cqw] w-[3cqw] shrink-0 bg-[var(--color-landing-gray-200)]'} />
                    <span className="h-[1cqw] flex-1 bg-[var(--color-landing-gray-200)]" />
                  </div>
                ))}
                <span className="mt-[1cqw] block bg-[var(--color-landing-primary)] py-[1.4cqw]" />
              </div>
            </div>
          </div>
        </div>
      </Parallax>

      {/* The four Office apps, on white tiles */}
      <div className="absolute bottom-[8%] left-[9%] flex gap-[2.4cqw]">
        {APPS.map((app) => (
          <span key={app} className="flex h-[11cqw] w-[11cqw] items-center justify-center bg-white shadow-[0_16px_30px_-18px_rgb(40_0_80_/_0.6)]">
            <OfficeAppIcon app={app} className="h-[64%] w-[64%]" />
          </span>
        ))}
      </div>
    </div>
  )
}

/**
 * Image + text block: the product graphic bleeds to the left viewport edge,
 * the partners copy sits on the right-hand columns.
 */
export function PartnersSection() {
  return (
    <section aria-labelledby="partners-title" className="landing-section overflow-x-clip">
      <div className="mx-auto grid max-w-[120rem] items-center gap-12 lg:grid-cols-12 lg:gap-0">
        <div className="lg:col-span-6">
          <OfficeGraphic />
        </div>

        <div className="px-[clamp(1.5rem,2vw,2rem)] lg:col-span-5 lg:col-start-8 lg:pl-0">
          <TextReveal as="h2" className="landing-text-display-lg text-[var(--color-landing-gray-900)] dark:text-white">
            <span id="partners-title">
              Built into <Highlight color="purple">Microsoft 365.</Highlight>
            </span>
          </TextReveal>
          <ScrollReveal direction="none" delay={0.15}>
            <p className="mt-6 max-w-[44ch] border-l border-[var(--color-landing-gray-300)] pl-5 landing-text-body-lg dark:border-[var(--color-landing-gray-700)] text-[color-mix(in_srgb,var(--color-landing-gray-900)_66%,transparent)] dark:text-[color-mix(in_srgb,white_62%,transparent)]">
              Templatean lives where your documents already do: inside Word, Excel, PowerPoint and Outlook.
            </p>
          </ScrollReveal>
          <StaggerContainer className="mt-10 grid" amount={0.1}>
            {partners.map((partner) => (
              <StaggerItem key={partner.name} className="border-t border-[var(--color-landing-gray-200)] py-5 dark:border-[var(--color-landing-gray-800)]">
                <h3 className="font-[family-name:var(--font-landing-subdisplay)] text-lg font-bold uppercase tracking-[-0.01em] text-[var(--color-landing-gray-900)] dark:text-white">
                  {partner.name}
                </h3>
                <p className="mt-1.5 text-pretty landing-text-body-sm text-[var(--color-landing-gray-700)] dark:text-[var(--color-landing-gray-300)]">
                  {partner.description}
                </p>
              </StaggerItem>
            ))}
          </StaggerContainer>
          <ScrollReveal direction="none" delay={0.1}>
            <Link
              to="/platform/integrations"
              className="landing-cta landing-cta--solid landing-cta--md landing-btn-sweep mt-8"
            >
              See all integrations
              <ArrowRight aria-hidden className="h-4 w-4" />
            </Link>
          </ScrollReveal>
        </div>
      </div>
    </section>
  )
}
